/**
 * Pure parsing/derivation helpers for the ngit-ci dashboard.
 *
 * This module is deliberately free of DOM and relay dependencies so every
 * function is unit-testable in isolation (Gate 1/2). It maps raw Nostr events
 * (NIP-C1 kinds 39842 / 9841 / 9842) onto the dashboard's typed state.
 *
 * It also derives the display helpers the run list needs: which REPO and which
 * BRANCH a run belongs to (`a` / `r` tags) and, for a failed run, the first
 * meaningful error line out of the failing job's log tail.
 *
 * Event shapes follow NIP.md (see docs/architecture.md). Key associations:
 * - Workflow Progress (39842): run id in `d`, status in `status`, completed
 *   job refs are the `q` tags carrying a job-id marker.
 * - Job Result (9841): the run association is the `q` tag
 *   `39842:<coordinator>:<run-id>` — NOT a `d` tag.
 * - Workflow Result (9842): run id in the non-ref `r` tag (a second `r` tag
 *   `refs/...` is the Git ref and must be ignored).
 */
import type {
  Conclusion,
  JobStatus,
  JobResult,
  RawEvent,
  Trigger,
  WorkflowProgress,
  WorkflowResult,
  WorkflowStatus,
} from '../types';
import { EVENT_KINDS } from '../types';

/** Return the first value of the first tag whose name matches `name`. */
export function getFirstTag(tags: string[][], name: string): string | undefined {
  for (const tag of tags) {
    if (tag[0] === name) {
      return tag[1];
    }
  }
  return undefined;
}

/** Return every value of every tag whose name matches `name`. */
export function getAllTags(tags: string[][], name: string): string[][] {
  return tags.filter((tag) => tag[0] === name);
}

/** Parse a `q` tag into its NIP-18 quote parts. */
function parseQuote(tag: string[]): { eventId: string; relay?: string; pubkey?: string; marker?: string } {
  return {
    eventId: tag[1] ?? '',
    relay: tag[2],
    pubkey: tag[3],
    marker: tag[4],
  };
}

/** Parse a decimal unix timestamp tag value into a number, or undefined. */
function parseTimestamp(value: string | undefined): number | undefined {
  if (value === undefined) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

/** Non-job `q` tag markers that must not be treated as job refs (NIP-C1). */
const NON_JOB_MARKERS = new Set(['manual-trigger', 'service-request']);

/**
 * Parse a Workflow Progress event (kind 39842) into dashboard state.
 * `completedJobRefs` are the job ids quoted via `q` tags (each carries a
 * job-id marker); `inProgressJobs` come from `in-progress` tags. Non-job
 * markers (`manual-trigger`, `service-request`) are excluded.
 */
export function parseProgress(ev: RawEvent): WorkflowProgress {
  const tags = ev.tags;
  const status = (getFirstTag(tags, 'status') as WorkflowStatus | undefined) ?? 'queued';
  const inProgressJobs = getAllTags(tags, 'in-progress')
    .map((t) => t[1])
    .filter((v): v is string => Boolean(v));
  const completedJobRefs = getAllTags(tags, 'q')
    .map((t) => parseQuote(t).marker)
    .filter((v): v is string => Boolean(v) && !NON_JOB_MARKERS.has(v as string));

  return {
    kind: EVENT_KINDS.WORKFLOW_PROGRESS,
    id: ev.id,
    runId: getFirstTag(tags, 'd') ?? '',
    status,
    conclusion: status === 'concluded' ? (getFirstTag(tags, 'conclusion') as Conclusion | undefined) : undefined,
    inProgressJobs,
    completedJobRefs,
    commit: getFirstTag(tags, 'c') ?? '',
    trigger: getFirstTag(tags, 'o') as Trigger | undefined,
    startedAt: parseTimestamp(getFirstTag(tags, 'started_at')),
    ...parseRepoRef(tags),
    ...parseGitRef(tags),
    createdAt: ev.created_at,
  };
}

/**
 * Parse a Job Result event (kind 9841) into dashboard state.
 * The run association is the `q` tag `39842:<coordinator>:<run-id>`; the
 * run id is the third colon-separated segment.
 */
export function parseJobResult(ev: RawEvent): JobResult {
  const tags = ev.tags;
  const runRef = parseRunRefFromQuote(tags);

  return {
    kind: EVENT_KINDS.JOB_RESULT,
    id: ev.id,
    runRef,
    jobId: getFirstTag(tags, 'job') ?? '',
    name: getFirstTag(tags, 'name'),
    conclusion: getFirstTag(tags, 'conclusion') as Conclusion | undefined,
    logsUrl: getFirstTag(tags, 'logs'),
    queuedAt: parseTimestamp(getFirstTag(tags, 'queued_at')),
    startedAt: parseTimestamp(getFirstTag(tags, 'started_at')),
    exitCode: parseTimestamp(getFirstTag(tags, 'exit_code')),
    runsOn: getFirstTag(tags, 'runs_on'),
    logTail: ev.content,
    ...parseRepoRef(tags),
    ...parseGitRef(tags),
    createdAt: ev.created_at,
  };
}

/**
 * Extract the workflow run id from a Job Result's `q` tag of the form
 * `39842:<coordinator-pubkey>:<workflow-run-id>`. Returns '' when absent.
 */
function parseRunRefFromQuote(tags: string[][]): string {
  for (const tag of getAllTags(tags, 'q')) {
    const value = tag[1] ?? '';
    if (value.startsWith('39842:')) {
      const parts = value.split(':');
      if (parts.length >= 3) return parts[2] ?? '';
    }
  }
  return '';
}

/**
 * Parse a Workflow Result event (kind 9842) into dashboard state.
 * The run id is the non-ref `r` tag (a second `r` tag `refs/...` is the Git
 * ref and is ignored).
 */
export function parseWorkflowResult(ev: RawEvent): WorkflowResult {
  const tags = ev.tags;
  const runId = getAllTags(tags, 'r')
    .map((t) => t[1])
    .find((v) => v && !v.startsWith('refs/')) ?? '';
  // Each `q` tag is a Job Result quote: [event-id, relay, pubkey, job-id].
  // The event id is the per-instance key; the job-id marker is the
  // workflow-declared job id. Non-job markers are excluded.
  const jobRefs = getAllTags(tags, 'q')
    .map((t) => ({ eventId: t[1] ?? '', jobId: t[4] ?? '' }))
    .filter((r) => Boolean(r.eventId) && Boolean(r.jobId) && !NON_JOB_MARKERS.has(r.jobId));

  return {
    kind: EVENT_KINDS.WORKFLOW_RESULT,
    id: ev.id,
    runId,
    conclusion: (getFirstTag(tags, 'conclusion') as Conclusion) ?? 'neutral',
    queuedAt: parseTimestamp(getFirstTag(tags, 'queued_at')),
    startedAt: parseTimestamp(getFirstTag(tags, 'started_at')),
    commit: getFirstTag(tags, 'c') ?? '',
    trigger: getFirstTag(tags, 'o') as Trigger | undefined,
    jobRefs,
    ...parseRepoRef(tags),
    ...parseGitRef(tags),
    createdAt: ev.created_at,
  };
}

/**
 * Derive a job's display status. A job is "concluded" once it has a
 * conclusion; otherwise it is still "in_progress".
 */
export function deriveJobStatus(conclusion: Conclusion | undefined): JobStatus {
  return conclusion ? 'concluded' : 'in_progress';
}

/** Truncate a full 64-char hex commit id to a short 7-char display form. */
export function shortSha(sha: string): string {
  if (sha.length <= 7) return sha;
  return sha.slice(0, 7);
}

/**
 * Render a unix-epoch (seconds) timestamp as a compact relative time string.
 * `nowMs` is the reference "now" in milliseconds (injectable for tests).
 */
export function relativeTime(epochSeconds: number, nowMs: number = Date.now()): string {
  const diffMs = nowMs - epochSeconds * 1000;
  if (diffMs < 0) return 'just now';
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

/* ------------------------------------------------------------------------- *
 * Repo address (`a`) + git ref (`r`) derivation.
 *
 * NIP-C1 carries the repository on every CI event as a NIP-34 address tag
 * `a = 30617:<owner-pubkey>:<repo-id>` and the git ref as an `r` tag
 * `refs/heads/<branch>`. The run list must name the repo and the branch for
 * every run, so these are derived once, purely, and carried onto the typed
 * events (and onto `RunState`). A missing tag degrades to '' — never
 * `undefined`, never a throw.
 * ------------------------------------------------------------------------- */

/** Number of owner-pubkey characters shown as the short repo owner. */
export const OWNER_SHORT_LENGTH = 8;

/** Shorten an owner pubkey to its first 8 chars ('' when absent). */
export function shortOwner(pubkey: string | undefined): string {
  if (!pubkey) return '';
  return pubkey.slice(0, OWNER_SHORT_LENGTH);
}

/** Parsed NIP-34 repository address (`a` tag). */
export interface RepoRef {
  /** Raw `a` tag value, or '' when the event carries no repo address. */
  repoAddr: string;
  /** Repository id (third colon segment), or ''. */
  repoId: string;
  /** Short owner: first 8 chars of the repo owner pubkey, or ''. */
  repoOwner: string;
}

/** Parse the first `a` repo-address tag into `{ repoAddr, repoId, repoOwner }`. */
export function parseRepoRef(tags: string[][]): RepoRef {
  const repoAddr = getFirstTag(tags, 'a') ?? '';
  if (!repoAddr) return { repoAddr: '', repoId: '', repoOwner: '' };
  const parts = repoAddr.split(':');
  return {
    repoAddr,
    repoId: parts.length >= 3 ? (parts[2] ?? '') : '',
    repoOwner: shortOwner(parts[1]),
  };
}

/**
 * Render a git ref as a display branch: `refs/heads/x` -> `x`. Non-branch refs
 * stay readable by dropping only the `refs/` prefix (`refs/tags/v1` ->
 * `tags/v1`, `refs/pull/7/head` -> `pull/7/head`); a bare value is returned
 * as-is. '' stays ''.
 */
export function branchFromRef(ref: string): string {
  if (!ref) return '';
  if (ref.startsWith('refs/heads/')) return ref.slice('refs/heads/'.length);
  if (ref.startsWith('refs/')) return ref.slice('refs/'.length);
  return ref;
}

/** Parsed git ref (`r` tag) with its display branch. */
export interface GitRef {
  /** Raw ref, e.g. `refs/heads/main`, or ''. */
  ref: string;
  /** Display branch (see `branchFromRef`), or ''. */
  branch: string;
}

/**
 * Parse the `refs/...` `r` tag as the git ref. On a Workflow Result there are
 * TWO `r` tags — the run id (non-ref) and the git ref — so only the `refs/`
 * one is the git ref; the run id keeps its own (unchanged) derivation.
 */
export function parseGitRef(tags: string[][]): GitRef {
  const ref =
    getAllTags(tags, 'r')
      .map((t) => t[1])
      .find((v): v is string => Boolean(v) && String(v).startsWith('refs/')) ?? '';
  return { ref, branch: branchFromRef(ref) };
}

/* ------------------------------------------------------------------------- *
 * Failure surfacing.
 *
 * A failed run must show WHAT failed without hunting: the run row carries a
 * short summary (failing job + exit code + the first MEANINGFUL error line)
 * and the failing job's log tail is expanded by default.
 * ------------------------------------------------------------------------- */

/** Maximum length of an error line surfaced in the UI. */
export const ERROR_LINE_MAX = 200;

/** Conclusions that mean "this run/job went wrong" for the UI. */
const FAILED_CONCLUSIONS = new Set<Conclusion>(['failure', 'startup_failure', 'timed_out']);

/** True when a conclusion counts as a failure in the UI. */
export function isFailure(conclusion: Conclusion | undefined): boolean {
  return conclusion !== undefined && FAILED_CONCLUSIONS.has(conclusion);
}

/** ANSI SGR colour codes that decorate coordinator job logs. */
const ANSI_SGR = /\u001b\[[0-9;]*m/g;
/** The coordinator's log-truncation marker (`[log-tail omitted=4123]`). */
const TRUNCATION_MARKER = /^\[log-tail omitted=\d+\]$/;
/** A line made only of whitespace or decorative rule/box characters. */
const DECORATIVE_LINE = /^[\s\-_=*~#|+\u00b7\u2022\u2500\u2550\u2591\u2592\u2593<>^]+$/;
/**
 * act / GitHub-Actions progress chrome: banners, image/docker progress, command
 * echoes and workflow-command annotations. Real log tails start with these and
 * they are never the error the operator is looking for. Error markers
 * (`❌`, `✗`, `::error`, `FAIL`, …) are deliberately NOT listed here.
 */
const ACT_CHROME_LINE =
  /^(?:⭐|🚀|🐳|🏁|🎬|⚙|🔍|⏭|✔|✅|▶|\[command\]|\[\.\.\.|##\[|::(?:set-output|save-state|add-path|add-mask|debug|group|endgroup|notice|echo))/u;

/**
 * First meaningful line of a log tail: skips blank / whitespace-only lines,
 * the `[log-tail omitted=N]` truncation marker, decorative rules (tables) and
 * act/GitHub-Actions progress chrome (`⭐ Run Set up job` banners, `🚀`/`🐳`
 * image progress, `[command]` echoes, `::set-output` annotations), strips ANSI
 * colour codes, trims, and caps the length at `ERROR_LINE_MAX`. Returns '' when
 * nothing meaningful is left (or there is no log tail at all) — the caller then
 * falls back to `fallbackErrorLine`, so a failure is never silent.
 */
export function firstMeaningfulLine(logTail: string | undefined): string {
  if (!logTail) return '';
  for (const raw of logTail.split('\n')) {
    const line = raw.replace(ANSI_SGR, '').trim();
    if (!line) continue;
    if (TRUNCATION_MARKER.test(line)) continue;
    if (DECORATIVE_LINE.test(line)) continue;
    if (ACT_CHROME_LINE.test(line)) continue;
    return line.length > ERROR_LINE_MAX ? `${line.slice(0, ERROR_LINE_MAX - 1)}\u2026` : line;
  }
  return '';
}

/**
 * Readable error line for a conclusion whose job produced NO usable log output
 * (e.g. `startup_failure`, which has no log tail at all). Never blank.
 */
export function fallbackErrorLine(conclusion: Conclusion | undefined): string {
  switch (conclusion) {
    case 'startup_failure':
      return 'startup failure: the job never started (no log output)';
    case 'timed_out':
      return 'timed out with no log output';
    case 'failure':
      return 'no error output captured (failure)';
    default:
      return `no error output captured (${conclusion ?? 'unknown conclusion'})`;
  }
}

/** Minimal job shape needed to summarise a failure (structurally a `Job`). */
export interface FailureJobInput {
  id?: string;
  jobId?: string;
  name?: string;
  conclusion?: Conclusion;
  exitCode?: number;
  logTail?: string;
}

/** Short, render-ready summary of why a run failed. */
export interface RunErrorSummary {
  /** Display name of the failing job (falls back to job id, event id, `job`). */
  jobName: string;
  /** Exit code of the failing job, when the 9841 carried one. */
  exitCode?: number;
  /** First meaningful error line, or a readable fallback. Never blank. */
  errorLine: string;
}

/** Input for `summarizeRunError`: a run conclusion plus its jobs. */
export interface RunErrorInput {
  conclusion?: Conclusion;
  jobs?: Iterable<FailureJobInput>;
}

/**
 * Summarise why a run failed, or `undefined` when neither the run nor any job
 * failed. The failing job that carries a usable log line is preferred (so the
 * message is a real error, not an empty one), with ties broken by job id for a
 * deterministic result; a `startup_failure` job with no log tail still gets a
 * readable line.
 */
export function summarizeRunError(input: RunErrorInput): RunErrorSummary | undefined {
  const failing = [...(input.jobs ?? [])]
    .filter((job) => isFailure(job.conclusion))
    .sort((a, b) => (a.id ?? '').localeCompare(b.id ?? ''));
  if (!isFailure(input.conclusion) && failing.length === 0) return undefined;

  const job = failing.find((candidate) => firstMeaningfulLine(candidate.logTail)) ?? failing[0];
  if (!job) {
    return { jobName: 'run', errorLine: fallbackErrorLine(input.conclusion) };
  }
  const summary: RunErrorSummary = {
    jobName: job.name || job.jobId || job.id || 'job',
    errorLine: firstMeaningfulLine(job.logTail) || fallbackErrorLine(job.conclusion),
  };
  if (job.exitCode !== undefined) summary.exitCode = job.exitCode;
  return summary;
}

/** Format a run-error summary as the single line shown in `.run-error`. */
export function formatRunError(summary: RunErrorSummary): string {
  const exit = summary.exitCode !== undefined ? ` (exit ${summary.exitCode})` : '';
  return `${summary.jobName}${exit}: ${summary.errorLine.trim()}`;
}

/**
 * Map a conclusion to a stable CSS class token for styling.
 * success -> ok, failure -> fail, neutral -> neutral,
 * cancelled/timed_out/startup_failure -> warn, skipped -> skip.
 */
export function conclusionClass(conclusion: Conclusion | undefined): string {
  switch (conclusion) {
    case 'success':
      return 'ok';
    case 'failure':
      return 'fail';
    case 'neutral':
      return 'neutral';
    case 'cancelled':
    case 'timed_out':
    case 'startup_failure':
      return 'warn';
    case 'skipped':
      return 'skip';
    default:
      return 'neutral';
  }
}
